import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild, ElementRef, HostListener } from '@angular/core';
import { LabelMaps, LabelObject, Ranking } from 'globals/store/events.data';
import { Performer } from 'globals/store/performers.data';
import publicHelpers from 'globals/helpers/public-helpers';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { JoiFile } from 'globals/store/files.model';
import { EntityAsset, EntityRequest } from 'globals/store/programs.data';
import { InformableSectionItem } from 'globals/store/informables.data';
import { Sponsor } from 'globals/store/sponsors.data';
import { Asset } from 'globals/store/assets.data';
import { TagPanelData } from 'globals/store/tags.data';
import { sleepForMS } from 'src/app/helpers/helpers';
import { OnsiteBrickType } from 'globals/store/onsite.data';
import { Dictionary } from '@ngrx/entity';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MilitaryTimePipe } from 'src/app/services/pipes/militaryTime.pipe';
import { ExpandingWrapperComponent } from '../wrappers/expanding-wrapper.component';
import { AssetLinkComponent } from 'src/app/modules/assets/asset-link.component';
import { FileLinkComponent } from '../file-handler/file-link.component';
import { IconComponent } from '../icon/icon.component';
import { LabelItemComponent } from '../label-marker/label-item.component';
import { UserSelectedPublishTimezoneTextPipe } from 'src/app/services/pipes/timezone.pipe';
import { ExpandIconComponent } from '../icon/expand.icon.component';
import { TypeofPipe } from 'src/app/services/pipes/typeof.pipe';
import { StaticPerformerCardsComponent } from '../performers/static-performer-cards.component';
import { StaticSponsorCardsComponent } from 'src/app/modules/sponsors/static-sponsor-cards.component';
import { TagPopupComponent } from 'src/app/modules/onsiteschedule/tag-popup.component';
import { ExpandableInfoComponent } from '../information/expandable-info.component';
import { StagingRequestsComponent } from 'src/app/modules/manage/staging-requests.component';

@Component({
    selector: 'timeline-item',
    templateUrl: './timeline-item.html',
    styleUrls: ['timeline-item.scss'],
    animations: [
        trigger('drop', [
            state('expand', style({ height: '*' })),
            state('collapse, undefined, void', style({ height: '0px' })),
            transition('* => *', [animate('300ms')]),
            transition('void => *', [animate('0ms')]),
            transition('undefined => *', [animate('0ms')]),
        ]),
    ],
    standalone: true,
    imports: [MatCheckboxModule, CommonModule, StagingRequestsComponent, ExpandableInfoComponent, TagPopupComponent, StaticPerformerCardsComponent, StaticSponsorCardsComponent, TypeofPipe, ExpandIconComponent, LabelItemComponent, UserSelectedPublishTimezoneTextPipe, IconComponent, FileLinkComponent, CommonModule, AssetLinkComponent, MilitaryTimePipe, ExpandingWrapperComponent ],
})
export class TimelineItemComponent implements OnInit, OnChanges {
  @Input() start: string
  @Input() end: string
  @Input() timezoneAbbr: string;
  @Input() timeZoneId: string
  @Input() epoch: number;
  @Input() userSelectedTimezone: any
  @Input() hideDot: boolean
  @Input() duration: number
  @Input() printDate?: string | null
  @Input() first = false
  @Input() last = false
  @Input() title: string
  @Input() description: string
  @Input() labelMap?: LabelObject = {}
  @Input() labelMaps?: LabelMaps
  @Input() labelIds: string[]
  @Input() performers: { [key: string]: Performer }
  @Input() publicView = false
  @Input() militaryTime = true
  @Input() performer: Performer // the amount of inputs on this is getting insane.  I need to accept a performer so that I can include the performer's name in "Receive presentation assets from xxx", and assetInfo so that the toggle works.  grr
  @Input() performerIds: string[]
  @Input() sponsors: { [key: string]: Sponsor }
  @Input() sponsorIds: string[]
  @Input() sponsorTypes: { [tId:string]: Ranking }
  @Input() sponsorsCustomTitle?: string
  @Input() hideTimeLine = false
  @Input() programLine: string
  @Input() link = false
  @Input() standalone = false
  @Input() brief = false
  @Input() collapsible = false
  @Input() files: JoiFile[]
  @Input() publicLinks: JoiFile[]
  @Input() stagingRequests: EntityRequest[]
  @Input() stagingNotes: string
  @Input() presentationNotes: InformableSectionItem[]
  @Input() presentationAssets: Asset[]
  @Input() otherAssets: Asset[]
  @Input() tagData: TagPanelData
  @Input() multi = false
  @Input() timeAbove = false
  @Input() isBreak = false
  @Input() startingCollapseState: 'expand' | 'collapse' = 'expand'
  collapseState: 'expand' | 'collapse'
  @Input() forcedCollapseState
  @Input() manualCollapse = false
  @Input() hideTimeZone = false
  @Input() checking: boolean
  @Input() checked: boolean
  @Input() type: OnsiteBrickType = 'session'

  @Input() performerRoles: Dictionary<Ranking>
  @Input() performerRoleIds: Dictionary<string>

  @Output() onCheck: EventEmitter<void> = new EventEmitter()

  scheduleName = ''
  fullLocation = ''
  helpers = publicHelpers
  showExpand = false
  popupDir = 0

  @Input() condensedPerformers = true
  @Input() activityIncomplete = false // need states for both because it is possible that this boolean state is filtered out entirely by deselecting "activity completed status" from the fields menu
  @Input() activityComplete = false
  @Input() hidePerformersHeading = false
  @Input() narrowPerformersHeading = false

  @Output() selectItem: EventEmitter<null> = new EventEmitter() // these emitters will fire in share mode, but nothing is listening to them so it doesn't matter
  @Output() selectProgram: EventEmitter<null> = new EventEmitter()
  @Output() onToggleCollapse: EventEmitter<null> = new EventEmitter()

  @ViewChild('bodyContainer', { static: true }) bodyContainer: ElementRef
  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    // This is a bugged implementation of this (by design for now).  This toggles the current expanded state of a timeline item, so if some are open and some are closed, they will flip instead of all becoming open or closed.  Recommend two new event emitters that are paid attention to (ultimately) by public views
    if(["ArrowUp","ArrowDown"].includes(event.key) && event.ctrlKey){
      this.toggleCollapse(event)
    }
  }

  ngOnInit() {
    // console.log({ labelIds: this.labelIds, labelMap: this.labelMap })
    if (this.brief) {
      const [ scheduleName, fullLocation ] = this.programLine.split(' • ') // programLine is already properly formatted and I don't want to add even more possible inputs to this component
      this.scheduleName = scheduleName
      this.fullLocation = fullLocation
      // const { performers, performerIds } = this
      // console.log(scheduleName, { fullLocation, performers, performerIds })
    }
    if (this.type === 'activity' && this.brief) {
      this.collapseState = 'collapse'
    } else {
      this.collapseState = this.startingCollapseState
    }
  }

  toggleCollapse(event) {
    event.stopPropagation()
    this.collapseState = this.collapseState === 'expand' ? 'collapse' : 'expand'
    this.onToggleCollapse.emit()
  }

  expand() {
    this.collapseState = 'expand'
  }

  collapse() {
    this.collapseState = 'collapse'
  }

  ngOnChanges() {

    // this is the simplest way to determine if there is anything to display within the body of the timeline item.  The design of the timeline item MUST take this into account (no empty divs within bodyContainer!!).  This also can't be put onto the body itself as it causes expression changed errors
    if (!this.collapsible && !this.brief) return
    setTimeout(async () => {
      while (!this.bodyContainer) {
        await sleepForMS(1000)
      }

      const bodyContainer = this.bodyContainer.nativeElement
      if (!bodyContainer) return
      const hasChildren = !!bodyContainer.children.length
      this.showExpand = hasChildren
      if (!hasChildren) { // this might need to be removed - my thinking is it makes sense for things that appear collapsed to stay collapsed after changing things around such that they could be expanded again
        this.collapseState = 'collapse'
      }
    },0)
  }

  clickProgram(event) {
    if (!this.standalone) {
      event.stopPropagation();
      this.selectProgram.emit();
    }
  }

}

